import { useCallback, useEffect, useMemo, useState } from "react";
import api, { pickErr } from "@/lib/api";
import { notifyError, notifySuccess } from "@/lib/notify";

function isExpired(dateStr) {
  if (!dateStr) return false;
  // dateStr is YYYY-MM-DD; compare as date strings against today (UTC date).
  const today = new Date().toISOString().slice(0, 10);
  return dateStr < today;
}

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [code, setCode] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [usageLimit, setUsageLimit] = useState(10);
  const [expiryDate, setExpiryDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Edit-coupon modal state
  const [editCoupon, setEditCoupon] = useState(null);
  const [editLimit, setEditLimit] = useState("");
  const [editExpiry, setEditExpiry] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");

  const schoolNameById = useMemo(() => {
    const m = {};
    schools.forEach((s) => (m[s.id] = s.name));
    return m;
  }, [schools]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [cs, ss] = await Promise.all([
        api.get("/coupons"),
        api.get("/schools"),
      ]);
      setCoupons(Array.isArray(cs.data) ? cs.data : []);
      setSchools(Array.isArray(ss.data) ? ss.data : []);
    } catch (err) {
      setError(pickErr(err, "Failed to load coupons"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e) {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      await api.post("/coupons", {
        code: code.trim(),
        assigned_school_id: schoolId,
        usage_limit: Number(usageLimit),
        expiry_date: expiryDate,
      });
      setCode("");
      setSchoolId("");
      setUsageLimit(10);
      setExpiryDate("");
      notifySuccess("Coupon created");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to create coupon");
      setFormError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/coupons/${id}`);
      notifySuccess("Coupon deleted");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to delete coupon");
      setError(msg);
      notifyError(msg);
    }
  }

  function openEdit(c) {
    setEditCoupon(c);
    setEditLimit(String(c.usage_limit ?? ""));
    setEditExpiry(c.expiry_date ? c.expiry_date.slice(0, 10) : "");
    setEditError("");
  }

  function closeEdit() {
    if (editBusy) return;
    setEditCoupon(null);
    setEditError("");
  }

  async function onSaveEdit(e) {
    e.preventDefault();
    if (!editCoupon) return;
    setEditError("");
    setEditBusy(true);
    try {
      const payload = {};
      const lim = Number(editLimit);
      if (!Number.isNaN(lim) && lim !== editCoupon.usage_limit) {
        payload.usage_limit = lim;
      }
      if (editExpiry && editExpiry !== editCoupon.expiry_date) {
        payload.expiry_date = editExpiry;
      }
      if (Object.keys(payload).length === 0) {
        setEditError("Nothing to update");
        setEditBusy(false);
        return;
      }
      await api.put(`/coupons/${editCoupon.id}`, payload);
      notifySuccess("Coupon updated");
      setEditCoupon(null);
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to update coupon");
      setEditError(msg);
      notifyError(msg);
    } finally {
      setEditBusy(false);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Coupons</h2>
      <p className="text-sm text-slate-500 mb-6">
        Issue and manage test-assignment coupons.
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Create a coupon
        </h3>
        <form
          onSubmit={onCreate}
          className="grid sm:grid-cols-4 gap-3 items-end"
          data-testid="coupons-create-form"
        >
          <div>
            <label className="block text-xs text-slate-600 mb-1">Code</label>
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="coupons-create-code"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">School</label>
            <select
              required
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              data-testid="coupons-create-school"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Select school</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Usage limit
            </label>
            <input
              type="number"
              min={1}
              required
              value={usageLimit}
              onChange={(e) => setUsageLimit(e.target.value)}
              data-testid="coupons-create-limit"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Expiry</label>
            <input
              type="date"
              required
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              data-testid="coupons-create-expiry"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="sm:col-span-4 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="coupons-create-submit"
              className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Create coupon"}
            </button>
            {formError ? (
              <p
                data-testid="coupons-create-error"
                className="text-sm text-red-600"
              >
                {formError}
              </p>
            ) : null}
          </div>
        </form>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            All coupons ({coupons.length})
          </h3>
          <button
            onClick={load}
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
        ) : error ? (
          <p
            data-testid="coupons-load-error"
            className="px-5 py-8 text-sm text-red-600"
          >
            {error}
          </p>
        ) : coupons.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">No coupons yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-5 py-2 font-medium">Code</th>
                <th className="px-5 py-2 font-medium">School</th>
                <th className="px-5 py-2 font-medium">Limit</th>
                <th className="px-5 py-2 font-medium">Used</th>
                <th className="px-5 py-2 font-medium">Expiry</th>
                <th className="px-5 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const expired = isExpired(c.expiry_date);
                return (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-5 py-2 text-slate-900 font-mono">
                      {c.code}
                    </td>
                    <td className="px-5 py-2 text-slate-600">
                      {schoolNameById[c.assigned_school_id] || c.assigned_school_id}
                    </td>
                    <td className="px-5 py-2 text-slate-700">{c.usage_limit}</td>
                    <td className="px-5 py-2 text-slate-700">{c.used_count}</td>
                    <td className="px-5 py-2 text-slate-700">
                      {c.expiry_date}
                      {expired ? (
                        <span
                          className="ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700"
                          data-testid={`coupons-expired-${c.id}`}
                        >
                          EXPIRED
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => openEdit(c)}
                          data-testid={`coupons-edit-${c.id}`}
                          className="text-xs font-medium text-indigo-700 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(c.id)}
                          data-testid={`coupons-delete-${c.id}`}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ---------- Edit Coupon Modal ---------- */}
      {editCoupon ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          data-testid="coupons-edit-modal"
          onClick={closeEdit}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white shadow-lg"
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-900">
                Edit Coupon
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Code{" "}
                <span className="font-mono text-slate-700">
                  {editCoupon.code}
                </span>{" "}
                · Used{" "}
                <span className="text-slate-700">{editCoupon.used_count}</span>
              </p>
            </div>

            <form onSubmit={onSaveEdit} className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Usage limit
                </label>
                <input
                  type="number"
                  min={Math.max(1, editCoupon.used_count || 1)}
                  required
                  value={editLimit}
                  onChange={(e) => setEditLimit(e.target.value)}
                  data-testid="coupons-edit-limit"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Cannot be lower than current used count ({editCoupon.used_count}).
                </p>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Expiry date
                </label>
                <input
                  type="date"
                  required
                  value={editExpiry}
                  onChange={(e) => setEditExpiry(e.target.value)}
                  data-testid="coupons-edit-expiry"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              {editError ? (
                <p
                  data-testid="coupons-edit-error"
                  className="text-sm text-red-600"
                >
                  {editError}
                </p>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={editBusy}
                  data-testid="coupons-edit-cancel"
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editBusy}
                  data-testid="coupons-edit-save"
                  className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-1.5 hover:bg-slate-800 disabled:opacity-60"
                >
                  {editBusy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
